import { useState, useCallback, useEffect, useMemo } from 'react'
import { useAppsNavigate, useAppsSearchParams } from '@wellesley/router'
import { useSearch } from '../common/hooks/useSearch'
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  Form,
  useIsMobile,
} from '@wellesley/ui'
import { InteractionGuard } from 'components/InteractionGuard'
import { SearchInput } from '../common/components/SearchInput'
import { useRecentSearches } from '../common/hooks/useRecentSearches'
import { DefaultSearchTrigger } from '../common/components/DefaultSearchTrigger'
import {
  SEARCH_CHANNEL_KEY,
  SEARCH_GROUP_KEY,
  SEARCH_VALUE_KEY,
} from '../common/const/searches'
import { SearchPopoverProps } from './types'
import { useSearchState } from './hooks/useSearchState'
import { useContextChip } from '../common/hooks/useContextChip'
import { useSearchHandlers } from './hooks/useSearchHandlers'
import { useRecentSearchHandlers } from './hooks/useRecentSearchHandlers'
import { SearchResultsRenderer } from './components/SearchResultsRenderer'
import { useGroup } from '@wellesley/group'
import { hasAnyResults } from './helpers/searchUtils'
import { FooterCaption } from './components/FooterCaption'
import {
  buildSearchParams,
  createGroupRecentSearch,
} from '../common/helpers/recentSearchHelpers'
import { isHashSearch } from '../common/helpers/isHashSearch'
import { SearchFilterValue } from '../common/types/filters'

const SearchPopoverUI = ({
  children,
  onResultClick,
  groupName,
  onAllResultsClick,
  defaultSearchInput = '',
  inThisContext = false,
  resetOnClose = false,
  onCancel,
}: SearchPopoverProps) => {
  const [searchParams, setSearchParams] = useAppsSearchParams()
  const [groupToSearch, setGroupToSearch] = useState<string | undefined>(
    groupName,
  )

  const { group } = useGroup({ groupname: groupToSearch || '' })

  const isGroupSearch = Boolean(groupToSearch)

  const {
    isOpen,
    setIsOpen,
    searchInput,
    setValue,
    showContextChip,
    setShowContextChip,
    userDismissedChip,
    setUserDismissedChip,
    handleClearInput: baseHandleClearInput,
    form,
    resetToDefaults,
  } = useSearchState({
    defaultSearchInput,
    resetOnClose,
  })

  const { recentSearches, addRecentSearch } = useRecentSearches()

  const selectedFilters = useMemo(() => {
    return [
      'groups',
      'people',
      ...(isHashSearch(searchInput) ? ['tags'] : []),
    ] as SearchFilterValue[]
  }, [searchInput])

  const { mainData, elsewhereData, loading } = useSearch({
    searchInput,
    selectedFilters,
    groupId: group?.groupId,
    limit: 8,
  })

  const hasMainResults = hasAnyResults(mainData)
  const hasElsewhereResults = hasAnyResults(elsewhereData)

  const handleReset = useCallback(() => {
    resetToDefaults()
    setGroupToSearch(groupName)
    setUserDismissedChip(false)
  }, [resetToDefaults, groupName])

  const handleCloseWithReset = useCallback(() => {
    if (resetOnClose) {
      handleReset()
    } else {
      baseHandleClearInput()
    }
    setIsOpen(false)
  }, [resetOnClose, handleReset, baseHandleClearInput])

  useEffect(() => {
    if (isOpen && resetOnClose) {
      setGroupToSearch(groupName)
      setUserDismissedChip(false)
    }
  }, [isOpen, resetOnClose, groupName])

  useEffect(() => {
    const hasContext = groupToSearch
    if (hasContext && !userDismissedChip) {
      setShowContextChip(true)
    } else {
      setShowContextChip(false)
    }
  }, [groupToSearch, userDismissedChip])

  const { contextChip } = useContextChip({
    groupName: groupToSearch,
    showContextChip,
    inThisContext: inThisContext,
    onClose: () => {
      if (groupToSearch && group) {
        addRecentSearch([
          createGroupRecentSearch(
            group,
            group.groupName ?? groupToSearch,
            searchInput.trim() || undefined,
          ),
        ])
      }
      setUserDismissedChip(true)
      setGroupToSearch(undefined)

      const newParams = new URLSearchParams(searchParams)
      newParams.delete(SEARCH_GROUP_KEY)
      newParams.delete(SEARCH_CHANNEL_KEY)
      setSearchParams(newParams)
    },
  })

  const { handleRecentSearchClick } = useRecentSearchHandlers({
    setValue,
    setGroupToSearch,
    setUserDismissedChip,
    addRecentSearch,
  })

  const { handleSuggestionClick, handleAllResultsClick, handleKeyDown } =
    useSearchHandlers({
      searchInput,
      groupName: groupToSearch,
      foundElsewhere: !hasMainResults && hasElsewhereResults,
      addRecentSearch,
      onClose: handleCloseWithReset,
      onResultClick,
      onAllResultsClick,
    })

  return (
    <Dialog modal={true} open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children || (
          <DefaultSearchTrigger placeholder="Search" onCancel={onCancel} />
        )}
      </DialogTrigger>

      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="h-[80vh] max-w-[600px] overflow-hidden bg-transparent shadow-none"
        onCloseModal={handleCloseWithReset}
        childrenClassName="p-none"
      >
        <div className="flex max-h-[80vh] flex-col rounded-m bg-white pb-xs pt-xxs shadow-lg">
          <div className="flex flex-shrink-0 flex-col gap-s rounded-t-m border-b bg-white px-m pb-xs">
            <Form {...form}>
              <SearchInput
                chips={contextChip || []}
                value={searchInput}
                control={form.control}
                onKeyDown={handleKeyDown}
                onClose={handleCloseWithReset}
                onClear={baseHandleClearInput}
              />
            </Form>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-m py-xxs">
            <SearchResultsRenderer
              loading={loading}
              mainData={mainData}
              groupName={groupToSearch}
              elsewhereData={elsewhereData}
              showElsewhereResults={isGroupSearch}
              recentSearches={recentSearches}
              searchInput={searchInput}
              onRecentSearchClick={handleRecentSearchClick}
              onSuggestionClick={handleSuggestionClick}
              onAllResultsClick={handleAllResultsClick}
            />
          </div>
          <div className="flex-shrink-0">
            <FooterCaption />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export const SearchPopover = (props: SearchPopoverProps) => {
  const [searchParams] = useAppsSearchParams()
  const navigate = useAppsNavigate()
  const isMobile = useIsMobile()
  const groupName = props?.groupName || searchParams.get(SEARCH_GROUP_KEY) || ''
  const defaultSearchInput = searchParams.get(SEARCH_VALUE_KEY) || ''

  const handleMobileTriggerClick = useCallback(() => {
    navigate('/search', {
      queryParams: buildSearchParams({
        query: defaultSearchInput,
        groupName: groupName || undefined,
      }),
    })
  }, [navigate, defaultSearchInput, groupName])

  if (isMobile && props.children) {
    return (
      <InteractionGuard when={true} onInteract={handleMobileTriggerClick}>
        {props.children}
      </InteractionGuard>
    )
  }

  return (
    <SearchPopoverUI
      groupName={groupName}
      defaultSearchInput={defaultSearchInput}
      inThisContext={Boolean(props?.groupName)}
      resetOnClose={true}
      {...props}
    />
  )
}
